import type { WebPushSubscriptionDto } from '@supply-mover-2-0/shared';
import { NotificationQueryService } from './NotificationQueryService';

/**
 * Which app's push a browser subscribes to (PD-16475). The CRM and the
 * storefront share this flow but not the service worker file or the API that
 * stores the subscription.
 */
export interface WebPushTargetModel {
	/** The service worker script, served at the site root. */
	serviceWorkerPath: string;
	/** The server's VAPID public key; empty when push is not configured. */
	getPublicKey: () => Promise<string>;
	/** Store this browser's subscription for the signed-in user. */
	register: (subscription: WebPushSubscriptionDto) => Promise<void>;
	/** Forget this browser's subscription for the signed-in user. */
	unregister: (endpoint: string) => Promise<void>;
}

/** The CRM user's push (`/sw.js`, `/api/notifications/me/push/*`). */
export const CRM_WEB_PUSH_TARGET: WebPushTargetModel = {
	serviceWorkerPath: '/sw.js',
	getPublicKey: async () =>
		(await NotificationQueryService.getVapidPublicKey()).publicKey,
	register: async (subscription) => {
		await NotificationQueryService.subscribe(subscription);
	},
	unregister: async (endpoint) => {
		await NotificationQueryService.unsubscribe(endpoint);
	},
};

/**
 * Browser push opt-in flow (PD-14301). Encapsulates the DOM-only pieces of
 * web-push — service-worker registration handshake, `Notification`
 * permission, and `PushManager` subscribe/unsubscribe — so the settings UI
 * stays declarative. Lives in web-ui (not core) because every method touches
 * `navigator` / `window` / `Notification`, which only exist in the browser.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class WebPushService {
	/** True only when the browser exposes the full web-push surface. */
	public static isBrowserPushSupported(): boolean {
		return (
			'serviceWorker' in navigator &&
			'PushManager' in window &&
			'Notification' in window
		);
	}

	/** Current notification permission, or 'unsupported' when the API is absent. */
	public static getPermission(): NotificationPermission | 'unsupported' {
		if (!('Notification' in window)) {
			return 'unsupported';
		}
		return Notification.permission;
	}

	/**
	 * iOS Safari only exposes web-push to home-screen-installed PWAs. When we
	 * detect iOS, an unsupported push surface, and a non-standalone display,
	 * the UI should explain the "Add to Home Screen" step instead of offering
	 * an enable button that can never succeed.
	 */
	public static isIosNeedsInstall(): boolean {
		// iPadOS 13+ reports as desktop Safari (no 'iPad' in the UA); the
		// MacIntel + multi-touch pair distinguishes a real touch iPad from a Mac.
		const isIpadOs =
			(navigator as { platform?: string }).platform === 'MacIntel' &&
			navigator.maxTouchPoints > 1;
		const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) || isIpadOs;
		if (!isIos) {
			return false;
		}
		const isStandalone =
			(navigator as { standalone?: boolean }).standalone === true ||
			window.matchMedia('(display-mode: standalone)').matches;
		return !isStandalone && !this.isBrowserPushSupported();
	}

	/**
	 * Request permission, subscribe via the active service worker using the
	 * server's VAPID key, and register the subscription with the API. Returns
	 * false when the browser can't push, permission is denied, or push isn't
	 * configured server-side.
	 */
	public static async enable(
		target: WebPushTargetModel = CRM_WEB_PUSH_TARGET,
	): Promise<boolean> {
		if (!this.isBrowserPushSupported()) {
			return false;
		}
		const permission = await Notification.requestPermission();
		if (permission !== 'granted') {
			return false;
		}
		const registration = await this._getReadyRegistration(
			target.serviceWorkerPath,
		);
		if (registration == null) {
			return false;
		}
		const publicKey = await target.getPublicKey();
		if (publicKey === '') {
			return false;
		}
		const existing = await registration.pushManager.getSubscription();
		const subscription =
			existing ??
			(await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: this._urlBase64ToUint8Array(publicKey),
			}));
		// PushSubscriptionJSON declares every field optional, but a live
		// subscription always serializes endpoint + keys — narrow instead of
		// casting so a malformed one can't produce a garbage payload.
		const json = subscription.toJSON();
		if (json.endpoint == null || json.keys == null) {
			return false;
		}
		await target.register({
			endpoint: json.endpoint,
			expirationTime: json.expirationTime,
			keys: {
				p256dh: json.keys['p256dh'] ?? '',
				auth: json.keys['auth'] ?? '',
			},
		});
		return true;
	}

	/** Unsubscribe this browser locally and remove it from the server. */
	public static async disable(
		target: WebPushTargetModel = CRM_WEB_PUSH_TARGET,
	): Promise<void> {
		const registration = await this._getReadyRegistration(
			target.serviceWorkerPath,
		);
		if (registration == null) {
			return;
		}
		const subscription = await registration.pushManager.getSubscription();
		if (subscription != null) {
			const endpoint = subscription.endpoint;
			await subscription.unsubscribe();
			await target.unregister(endpoint);
		}
	}

	/**
	 * Whether this browser currently holds a push subscription. Drives the
	 * enable/disable toggle state — permission alone is stale (it stays
	 * 'granted' after an unsubscribe). Uses getRegistration (not `.ready`) so it
	 * resolves fast even when no service worker is active.
	 */
	public static async isSubscribed(): Promise<boolean> {
		if (!this.isBrowserPushSupported()) {
			return false;
		}
		const registration = await navigator.serviceWorker.getRegistration('/');
		if (registration == null) {
			return false;
		}
		const subscription = await registration.pushManager.getSubscription();
		return subscription != null;
	}

	/**
	 * Resolve the active service-worker registration without hanging. Plain
	 * `navigator.serviceWorker.ready` never resolves if registration failed, so
	 * ensure one is registered and race readiness against a timeout.
	 */
	private static async _getReadyRegistration(
		serviceWorkerPath: string,
	): Promise<ServiceWorkerRegistration | null> {
		const existing = await navigator.serviceWorker.getRegistration('/');
		const registration =
			existing ??
			(await navigator.serviceWorker.register(serviceWorkerPath, {
				scope: '/',
			}));
		if (registration.active != null) {
			return registration;
		}
		const ready = navigator.serviceWorker.ready;
		const timeout = new Promise<null>((resolve) => {
			window.setTimeout(() => {
				resolve(null);
			}, 5000);
		});
		return Promise.race([ready, timeout]);
	}

	/**
	 * Decode a base64url-encoded VAPID public key into the raw byte array that
	 * `PushManager.subscribe` expects for `applicationServerKey`.
	 */
	private static _urlBase64ToUint8Array(base64String: string): Uint8Array {
		const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
		const base64 = (base64String + padding)
			.replace(/-/g, '+')
			.replace(/_/g, '/');
		const rawData = window.atob(base64);
		const outputArray = new Uint8Array(rawData.length);
		for (let i = 0; i < rawData.length; i++) {
			outputArray[i] = rawData.charCodeAt(i);
		}
		return outputArray;
	}
}
